import React, { useState, useEffect, useCallback, useRef } from 'react';
import { galleryPhotos } from '../data/gallery';

const Gallery: React.FC = () => {
    const photos = galleryPhotos;
    const [modalPhoto, setModalPhoto] = useState<string | null>(null);
    const [modalIndex, setModalIndex] = useState<number>(0);
    const [columns, setColumns] = useState<number>(4);
    const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
    const modalRef = useRef<HTMLDivElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        const updateColumns = () => {
            if (window.innerWidth < 600) setColumns(2);
            else if (window.innerWidth < 900) setColumns(3);
            else setColumns(4);
        };
        updateColumns();
        window.addEventListener('resize', updateColumns);
        return () => window.removeEventListener('resize', updateColumns);
    }, []);

    // Navegação por teclado no modal
    useEffect(() => {
        if (!modalPhoto) return;
        
        const handleKeyDown = (e: KeyboardEvent) => {
            switch (e.key) {
                case 'Escape':
                    setModalPhoto(null);
                    break;
                case 'ArrowLeft':
                    navigateModal(-1);
                    break;
                case 'ArrowRight':
                    navigateModal(1);
                    break;
            }
        };
        
        // Focus trap no modal
        closeButtonRef.current?.focus();
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleKeyDown);
        
        return () => {
            document.body.style.overflow = '';
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [modalPhoto, modalIndex]);

    const handleImageClick = (src: string, index: number) => {
        setModalPhoto(src);
        setModalIndex(index);
    };

    const navigateModal = (direction: number) => {
        const newIndex = (modalIndex + direction + photos.length) % photos.length;
        setModalIndex(newIndex);
        setModalPhoto(photos[newIndex]);
    };

    const handleImageLoad = useCallback((index: number) => {
        setLoadedImages(prev => new Set(prev).add(index));
    }, []);

    return (
        <section 
            className="gallery" 
            style={{ width: '100%', margin: '0 auto' }}
            aria-label="Galeria de fotos da turma"
        >
            <h1 className="text-4xl font-bold text-gold mb-8" style={{ textAlign: 'center', fontFamily: 'Playfair Display, serif' }}>
                Galeria de Fotos
            </h1>
            <div
                className="gallery-pinterest"
                style={{
                    columnCount: columns,
                    columnGap: '1.2rem',
                    padding: '1rem 0',
                }}
                role="list"
                aria-label={`${photos.length} fotos na galeria`}
            >
                {photos.map((photo, index) => (
                    <div
                        key={index}
                        className="gallery-item"
                        style={{
                            breakInside: 'avoid',
                            marginBottom: '1.2rem',
                            borderRadius: '14px',
                            overflow: 'hidden',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                            cursor: 'pointer',
                            background: '#181818',
                            position: 'relative',
                            minHeight: loadedImages.has(index) ? 'auto' : '200px',
                        }}
                        onClick={() => handleImageClick(photo, index)}
                        onKeyDown={(e) => e.key === 'Enter' && handleImageClick(photo, index)}
                        role="listitem"
                        tabIndex={0}
                        aria-label={`Foto ${index + 1} de ${photos.length}. Pressione Enter para ampliar.`}
                    >
                        {/* Skeleton placeholder */}
                        {!loadedImages.has(index) && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    background: 'linear-gradient(90deg, #1a1a1a 25%, #2a2a2a 50%, #1a1a1a 75%)',
                                    backgroundSize: '200% 100%',
                                    animation: 'shimmer 1.5s infinite',
                                    borderRadius: '14px',
                                }}
                            />
                        )}
                        <img
                            src={photo}
                            alt={`Foto ${index + 1}`}
                            className="gallery-image"
                            loading="lazy"
                            decoding="async"
                            onLoad={() => handleImageLoad(index)}
                            style={{
                                width: '100%',
                                height: 'auto',
                                display: 'block',
                                borderRadius: '14px',
                                objectFit: 'cover',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                                opacity: loadedImages.has(index) ? 1 : 0,
                                transition: 'opacity 0.3s ease-in-out',
                            }}
                        />
                    </div>
                ))}
            </div>
            {modalPhoto && (
                <div
                    ref={modalRef}
                    className="modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-label={`Foto ${modalIndex + 1} de ${photos.length} ampliada`}
                    onClick={(e) => e.target === e.currentTarget && setModalPhoto(null)}
                >
                    {/* Botão fechar */}
                    <button
                        ref={closeButtonRef}
                        className="modal-close-btn"
                        onClick={() => setModalPhoto(null)}
                        aria-label="Fechar visualização"
                    >
                        <i className="fas fa-times" aria-hidden="true"></i>
                    </button>
                    
                    {/* Navegação anterior */}
                    <button
                        onClick={() => navigateModal(-1)}
                        style={{
                            position: 'absolute',
                            left: '1rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'rgba(212, 160, 32, 0.8)',
                            color: '#000',
                            border: 'none',
                            borderRadius: '50%',
                            width: '48px',
                            height: '48px',
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                        aria-label="Foto anterior"
                    >
                        <i className="fas fa-chevron-left" aria-hidden="true"></i>
                    </button>
                    
                    <img
                        src={modalPhoto}
                        alt={`Foto ${modalIndex + 1} de ${photos.length} - Galeria da turma`}
                        style={{
                            maxWidth: '85vw',
                            maxHeight: '85vh',
                            border: '5px solid gold',
                            borderRadius: '16px',
                            boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
                        }}
                    />
                    
                    {/* Navegação próxima */}
                    <button
                        onClick={() => navigateModal(1)}
                        style={{
                            position: 'absolute',
                            right: '1rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'rgba(212, 160, 32, 0.8)',
                            color: '#000',
                            border: 'none',
                            borderRadius: '50%',
                            width: '48px',
                            height: '48px',
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                        aria-label="Próxima foto"
                    >
                        <i className="fas fa-chevron-right" aria-hidden="true"></i>
                    </button>
                    
                    {/* Indicador de posição */}
                    <div
                        style={{
                            position: 'absolute',
                            bottom: '1rem',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'rgba(0,0,0,0.7)',
                            color: '#d4a020',
                            padding: '0.5rem 1rem',
                            borderRadius: '20px',
                            fontSize: '0.9rem',
                        }}
                        aria-live="polite"
                    >
                        {modalIndex + 1} / {photos.length}
                    </div>
                </div>
            )}
        </section>
    );
};

export default Gallery;